// Regression test for the Esc-to-close handler added alongside the Manage
// Users gear dropdown (Plan 3 Task 19). ClickTarget's outside-click handler
// is also exercised here as a baseline.
import { render, unmountComponentAtNode } from 'react-dom'
import { act } from 'react-dom/test-utils'

import ClickTarget from './click-target.jsx'

let container: HTMLDivElement

beforeEach(() => {
  container = document.createElement('div')
  document.body.appendChild(container)
})

afterEach(() => {
  act(() => {
    unmountComponentAtNode(container)
  })
  container.remove()
})

const Target = ({ resetOnClickOutside = true }: { resetOnClickOutside?: boolean }) => (
  <ClickTarget resetOnClickOutside={resetOnClickOutside}>
    {(clicked: boolean) => (
      <div data-testid="state" data-clicked={String(clicked)}>
        {clicked ? 'open' : 'closed'}
      </div>
    )}
  </ClickTarget>
)

const open = () => {
  // The wrapping <span> handles clicks. Click any descendant — bubbles up.
  const target = container.querySelector<HTMLElement>('[data-testid="state"]')!
  act(() => {
    target.click()
  })
}

const stateText = () => container.querySelector('[data-testid="state"]')!.textContent

describe('ClickTarget — Esc closes the target (Plan 3 Task 19)', () => {
  test('Escape on document.body closes an open target', () => {
    act(() => {
      render(<Target />, container)
    })
    open()
    expect(stateText()).toBe('open')

    act(() => {
      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }))
    })
    expect(stateText()).toBe('closed')
  })

  test('Escape on a closed target is a no-op', () => {
    act(() => {
      render(<Target />, container)
    })
    expect(stateText()).toBe('closed')

    act(() => {
      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }))
    })
    expect(stateText()).toBe('closed')
  })

  test('non-Escape keys do not close', () => {
    act(() => {
      render(<Target />, container)
    })
    open()

    act(() => {
      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter' }))
      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'a' }))
      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Tab' }))
    })
    expect(stateText()).toBe('open')
  })

  test('resetOnClickOutside={false} opts out of the Esc handler', () => {
    act(() => {
      render(<Target resetOnClickOutside={false} />, container)
    })
    open()

    act(() => {
      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }))
    })
    expect(stateText()).toBe('open')
  })
})
